import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  TooltipProvider,
} from '@monksflow/monks-ui';
import { ChevronRightIcon, TagIcon, TagsIcon } from 'lucide-react';

import { CollapsibleSection, railRowClass, railRowIndent } from '@/shared/ui';

import type { MediaFacet } from '../api/facet-queries';
import { isTagSelected, type TagSelection } from '../lib/tag-selection';

/**
 * The tags the visible media carries, grouped by key.
 *
 * Grouped rather than listed flat because a value alone cannot say which facet it answers: `delivery`
 * is an activity and `mega` is a product, and a single list of words invites selecting them as though
 * they were the same kind of thing.
 *
 * Each group collapses, and starts collapsed, so the rail opens as a list of which facets exist
 * rather than every value under all of them.
 *
 * The term is the host's rather than this rail's: the column it sits in searches its folders with the
 * same words, and two fields asking the same question of one column would be a choice nobody wants to
 * make. It matches values, not keys -- typing a key name would return every value under it, which is
 * browsing rather than searching, and the group headings already show the keys.
 */
export const TagRail = ({
  facets,
  selection,
  itemsInScope,
  search = '',
  action,
  valueAction,
  renaming,
  onRenameCommit,
  onRenameCancel,
  onToggle,
}: {
  facets: readonly MediaFacet[];
  selection: TagSelection;
  /** How many rows the scope these facets were counted over holds. A facet covering all of them cannot narrow. */
  itemsInScope: number;
  /** What the host is filtering the whole rail by. Empty shows every facet. */
  search?: string;
  /** One control beside the heading. What a surface can do to tags depends on what it knows. */
  action?: ReactNode;
  /** One control on each value row. Return nothing for a row the host has nothing to offer on. */
  valueAction?: (key: string, value: string) => ReactNode;
  /**
   * Which row, if any, is currently being renamed in place.
   *
   * Controlled by the host rather than held here, because what opens it is the host's own menu and
   * what a committed name means is the host's too: the same words may be a rename or a merge, and
   * only the surface that can confirm the second one can tell them apart.
   */
  renaming?: { key: string; value: string };
  onRenameCommit?: (key: string, value: string, next: string) => void;
  onRenameCancel?: () => void;
  onToggle: (key: string, value: string) => void;
}) => {
  const { t } = useTranslation('tools');
  const [sectionOpen, setSectionOpen] = useState(true);

  const term = search.trim().toLowerCase();

  // A term arriving unfolds the section, on the same reasoning as a group opening onto a hit: results
  // behind a shut heading read as the search having found nothing. Only the moment it arrives, so the
  // section can still be folded away with a search running, and so clearing the term leaves it as the
  // person last left it.
  const searching = term.length > 0;
  const [wasSearching, setWasSearching] = useState(searching);
  if (searching !== wasSearching) {
    setWasSearching(searching);
    if (searching) {
      setSectionOpen(true);
    }
  }

  const visible = facets
    .map((facet) => ({
      ...facet,
      values: facet.values.filter((entry) => entry.value.toLowerCase().includes(term)),
    }))
    .filter((facet) => facet.values.length > 0);

  // A group whose values matched a search, or that holds a tag already chosen, must not keep them
  // hidden: hits behind a shut row, or a filter that is applied but invisible, both read as the
  // rail having lost something.
  // Presence of the key is the test, not a non-empty array: `TagSelection` drops a key when its
  // last value goes. Read off the selection rather than the facet's own values, because the server
  // leaves a facet's own chosen value out of its counts.
  const revealed = visible
    .filter((facet) => term.length > 0 || Object.hasOwn(selection, facet.key))
    .map((facet) => facet.key);

  // Merged into state when the reason to reveal changes, rather than unioned into `value` on every
  // render, so a group that opened itself can still be closed by hand; unioning would leave its
  // chevron a dead control. Adjusted during render rather than in an effect because an effect would
  // paint the group shut and reopen it a frame later. Compared as a joined string so an unstable
  // `facets` array cannot restart the sync and loop.
  const revealKey = revealed.join('\u0000');
  const [open, setOpen] = useState<string[]>(revealed);
  const [syncedReveal, setSyncedReveal] = useState(revealKey);
  if (revealKey !== syncedReveal) {
    setSyncedReveal(revealKey);
    setOpen((current) => [...new Set([...current, ...revealed])]);
  }

  return (
    // Its own provider rather than the app's: this rail is mounted by more than one host, and a
    // tooltip whose provider is missing throws rather than degrading.
    <TooltipProvider>
      <CollapsibleSection
        title={t('brandMedia.tags')}
        // Matching the folders beside it, a rank below the title the rail carries over both.
        size="sm"
        open={sectionOpen}
        onOpenChange={setSectionOpen}
        action={action}
      >
        <div className="flex flex-col gap-2">
          {term.length > 0 && visible.length === 0 && (
            <p className="px-3 text-sm text-muted-foreground">
              {t('brandMedia.noTagsMatch', { search: search.trim() })}
            </p>
          )}

          {/* Rows rather than an Accordion, which is a documentation control: it hangs its own
              chevron on the right, rules off every item and dims its siblings on hover. The rail
              beside this one is a tree of rows, and these are the same kind of thing. */}
          <div className="flex flex-col gap-1">
            {visible.map((facet) => {
              // One value covering everything in scope narrows nothing. Dimmed rather than dropped: a
              // facet that vanished would read as data missing rather than as a filter that would do
              // nothing.
              // A facet already chosen from is exempt, and the exemption is what makes the test
              // sound rather than merely kind: the scope it would be measured against is the one its
              // own value narrowed, so it covers all of it by definition. Dimming there would fade a
              // filter at the moment it was applied, and fade the only way back out of it.
              const inert =
                !Object.hasOwn(selection, facet.key) &&
                facet.values.length === 1 &&
                facet.values[0].count >= itemsInScope;
              const isOpen = open.includes(facet.key);

              return (
                <Collapsible
                  key={facet.key}
                  open={isOpen}
                  onOpenChange={(next) =>
                    setOpen((current) =>
                      next
                        ? [...current, facet.key]
                        : current.filter((entry) => entry !== facet.key),
                    )
                  }
                  role="group"
                  aria-label={facet.key}
                  data-inert={inert ? 'true' : undefined}
                  title={inert ? t('brandMedia.facetInert') : undefined}
                  className={`flex flex-col gap-1 ${inert ? 'opacity-45' : ''}`}
                >
                  <CollapsibleTrigger className={railRowClass(false)}>
                    <ChevronRightIcon
                      className={`size-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-out ${
                        isOpen ? 'rotate-90' : ''
                      }`}
                    />
                    <TagsIcon className="size-4 shrink-0" />
                    <span className="truncate">{facet.key}</span>
                    <span className="flex-1 tabular-nums text-muted-foreground">
                      {t('brandMedia.rowCount', { count: facet.values.length })}
                    </span>
                  </CollapsibleTrigger>

                  <CollapsibleContent className="flex flex-col gap-1">
                    {facet.values.map((entry) => {
                      const chosen = isTagSelected(selection, facet.key, entry.value);
                      const toggle = () => onToggle(facet.key, entry.value);

                      if (renaming?.key === facet.key && renaming.value === entry.value) {
                        return (
                          // Commits on blur with Enter blurring and Escape abandoning, which is how
                          // the folder rail above this one renames. The two sit in one column and a
                          // person should not have to learn them separately.
                          //
                          // Enter is spent here: a name already in use opens the merge dialog during
                          // this keypress, and the dialog's focused Cancel would otherwise receive
                          // the rest of it and close unseen.
                          <Input
                            key={entry.value}
                            ref={(input) => input?.select()}
                            size="sm"
                            defaultValue={entry.value}
                            aria-label={t('brandMedia.renameTagValue', { value: entry.value })}
                            style={{ marginLeft: railRowIndent(1) }}
                            onBlur={(event) =>
                              onRenameCommit?.(facet.key, entry.value, event.target.value)
                            }
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                event.currentTarget.blur();
                              }
                              if (event.key === 'Escape') onRenameCancel?.();
                            }}
                          />
                        );
                      }

                      return (
                        // A div rather than a button, on the same reasoning as a folder row: what
                        // the host hangs on the end of it is itself a button, and a button holding a
                        // button is invalid and stops the inner one receiving clicks in some
                        // browsers. One shape whether or not there is an action, so the rows cannot
                        // drift apart between the surfaces that mount this.
                        <div
                          key={entry.value}
                          role="button"
                          tabIndex={0}
                          aria-pressed={chosen}
                          onClick={toggle}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggle();
                            }
                          }}
                          className={`group ${railRowClass(chosen)}`}
                          // Indented like a folder's child, so a value reads as belonging to the key
                          // above it rather than as another key.
                          style={{ paddingLeft: railRowIndent(1) }}
                        >
                          {/* The width the key's chevron takes. Without it a value's icon would start
                              closer to the margin than its own heading's and the indent would stop
                              reading as one. */}
                          <span className="size-4 shrink-0" aria-hidden="true" />
                          <TagIcon className="size-4 shrink-0" />
                          <span className="truncate" title={entry.value}>
                            {entry.value}
                          </span>
                          {/* `flex-1` on the count rather than on the value: the count reads as part
                              of the label, so it sits against it and the slack goes after, which is
                              what leaves the action its own place at the end of the row. */}
                          <span className="flex-1 tabular-nums text-muted-foreground">
                            {t('brandMedia.rowCount', { count: entry.count })}
                          </span>
                          {valueAction?.(facet.key, entry.value)}
                        </div>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        </div>
      </CollapsibleSection>
    </TooltipProvider>
  );
};
